/**
 * platform/credentials.ts — THE OPERATOR CREDENTIAL SEAM, AND THE ONE THING IN
 * THIS APPLICATION THAT IS KNOWN TO BE WRONG.
 *
 * ===========================================================================
 * THE DEPENDENCY, STATED PLAINLY
 * ===========================================================================
 * Founder ruling 6, 2026-09-08:
 *
 *   > One credential must not authenticate scheduler jobs, global-knowledge
 *   > publication, platform Agent deployment and the tenant off-switch.
 *   > Human platform operations must use authenticated operator identity, NOT
 *   > an attribution header plus a broadly shared secret.
 *
 * This console is a human platform operation and it is built on exactly the
 * shape that ruling forbids, because that shape is the only door that exists
 * today: `SYSTEM_TRIGGER_TOKEN` for authentication and an `x-platform-operator`
 * header for attribution. `agent-runtime/platformDeployment.ts:929` already
 * says so in its own comment — the operator header is attribution, not
 * authentication. The console does not make that worse and it does not pretend
 * it is fixed. It CANNOT fix it: inventing a second operator-identity mechanism
 * while lane TOKENSPLIT is splitting the first one is how a product ends up
 * with two authorization models.
 *
 * SO THIS MODULE IS A SEAM, AND IT IS THE WHOLE OF THE SEAM. Everything the
 * console knows about who the operator is and how a request proves it lives
 * here and in `client.ts`. When a real operator session lands, `PlatformActor`
 * becomes whatever that session yields and no screen changes.
 *
 * ===========================================================================
 * WHAT THIS MODULE DOES ABOUT IT IN THE MEANTIME
 * ===========================================================================
 * Three properties, each of them a consequence of holding a shared secret in a
 * browser rather than an excuse for it:
 *
 *   1. NOTHING IS PERSISTED. No `localStorage`, no `sessionStorage`, no cookie,
 *      no IndexedDB. The credential lives in one module-local variable and dies
 *      with the tab. A shared platform secret written to disk on an operator's
 *      laptop is a credential leak with a long tail, and "the console
 *      remembered it for me" is not worth one.
 *   2. NOTHING IS LOGGED. The token is never interpolated into a message, an
 *      error, or a rendered node. `redacted()` is what any diagnostic gets.
 *   3. IT IS TYPED, so a future `PlatformActor` carrying a session id instead
 *      of a secret is a type change here and not a search across screens.
 *
 * The operator id is validated against the SAME pattern the server enforces
 * (`OPERATOR_ID_PATTERN` in `knowledge/global/routes.ts`), so "Alex from
 * compliance" is refused in the console with the reason rather than sent and
 * bounced with a 400 the operator has to interpret. The server remains the
 * guard; this is the message.
 */

/**
 * The identity the platform door attributes a publication to, and the secret it
 * authenticates with. TWO FIELDS BECAUSE THEY ARE TWO DIFFERENT THINGS — the
 * whole of ruling 6 is that they have been collapsed into one credential, and a
 * type that merged them would encode the defect.
 */
export interface PlatformActor {
  /**
   * ATTRIBUTION. Recorded on every publication, review and retirement. It is
   * not authentication and this field's name must not start claiming it is.
   */
  readonly operatorId: string;
  /**
   * AUTHENTICATION, today: the shared `SYSTEM_TRIGGER_TOKEN`. This is the field
   * ruling 6 retires. When TOKENSPLIT lands a publication-scoped credential, or
   * an operator session replaces it entirely, this is the field that changes.
   */
  readonly systemToken: string;
}

/** The server's own operator-id shape: an email or a service id, never prose. */
export const OPERATOR_ID_PATTERN = /^[A-Za-z0-9._@-]{1,64}$/;

export type ActorProblem =
  { field: 'operatorId'; message: string } | { field: 'systemToken'; message: string };

/**
 * Validate a would-be actor, or say which half is wrong and why.
 *
 * Returns problems as a LIST rather than the first failure: an operator filling
 * in two fields should be told about both, and a form that reveals its second
 * complaint only after the first is fixed teaches people to guess.
 */
export function actorProblems(operatorId: string, systemToken: string): ActorProblem[] {
  const problems: ActorProblem[] = [];
  const id = operatorId.trim();
  if (!id) {
    problems.push({
      field: 'operatorId',
      message: 'Every publication records who published it. Enter your operator identity.',
    });
  } else if (!OPERATOR_ID_PATTERN.test(id)) {
    problems.push({
      field: 'operatorId',
      message:
        'An operator identity is an email or a service id — letters, digits and . _ @ - only, ' +
        'up to 64 characters. Free text with spaces is not an ops identity.',
    });
  }
  if (!systemToken.trim()) {
    problems.push({
      field: 'systemToken',
      message: 'The platform credential is required. It is held in memory only and never stored.',
    });
  }
  return problems;
}

/**
 * Build an actor, or refuse. `null` when anything is wrong, so a caller cannot
 * end up holding a half-valid credential.
 */
export function makeActor(operatorId: string, systemToken: string): PlatformActor | null {
  if (actorProblems(operatorId, systemToken).length > 0) return null;
  return { operatorId: operatorId.trim(), systemToken: systemToken.trim() };
}

/**
 * What a diagnostic, a log line or a rendered node is allowed to know.
 *
 * The token is not partially revealed — no last-four, no length. A shared
 * secret's length is a fact about the secret, and this console has no use for
 * it that is worth printing.
 */
export function redacted(actor: PlatformActor): { operatorId: string; credential: string } {
  return { operatorId: actor.operatorId, credential: '[redacted]' };
}
